import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Row, Cell, Section } from "@sunwing/components";
import { RCLContext } from "../../components/RCL/RCLProvider";
import { RCL } from "../../components/RCL";
import { useRouting } from "../../hooks";
import { Breadcrumb } from "../../components/Breadcrumb";
import { useOktaAuth } from "@okta/okta-react";
import config from "../../app/config";
import { VWQIcon } from "@sunwing/icons";
import { GET_PAGE_SECTION } from "../../query/page.query";
import Grid from './grid';
import SectionCategories from "./SectionCategories";
import SectionContent from "./SectionContent";
import FaqSearchResult from "./FaqSearchResult";
import QuickLinkGrid from './QuickLinkGrid';
import { NewsFeed } from "../../components/NewsFeed";

let styles;

(async () => {
  if (config.isWestJetMode) {
    styles = await import("./helpfulinfoVwq.module.scss");
  } else {
    styles = await import("./helpfulinfo.module.scss");
  }
})();

const HelpfulInfo = () => {

  const { setCurrentRoute } = useRouting();
  const { locale } = useContext(RCLContext);
  const { oktaAuth } = useOktaAuth();
  const [helpfulInfoSections, setHelpfulInfoSections] = useState();
  const [quickLinks, setQuickLinks] = useState();
  const [selectedInfo, setSelectedInfo] = useState();
  const [selectedSection, setSelectedSection] = useState();
  const [searchResult, setSearchResult] = useState([]); 
  const [showSearchResult, setShowSearchResult] = useState(false); 
  const [searchText, setSearchText] = useState();
  const [faqDesc, setFaqDesc] = useState();

  const getHelpfulInfoPage = async () => {
    const query = `
      query pageData($locale: String, $skip:Int,$slug: String, $brandCode:String!) {
        pageCollection(locale: $locale, limit:100, skip:$skip, where: { slug: $slug brand: {brandCode:$brandCode} }) {
          items {
            sys {
              id
            }
            slug
            sectionsCollection(locale: $locale) {
              total
            }
          }
        }
      }`;

    await fetch(config.resourceServer.contentfulApimAgentGqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${oktaAuth.getAccessToken()}`,
      },
      body: JSON.stringify({
        query: query,
        variables: { 
          locale: locale === "fr" ? locale : "en-CA", 
          "skip":0,
          slug: "helpful-info",
          brandCode:config.brand ? config.brand.toUpperCase() : 'SWG'
        },
      }),
    })
      .then((response) => response.json())
      .then(({ data, errors }) => {
        if (errors) {
          console.error(errors);
        }else{
          getSectionItems(
            data.pageCollection?.items[0].sys.id, 
            data.pageCollection?.items[0].sectionsCollection.total, 
            0, 
            locale === "fr" ? locale : "en-CA");
        }
      });
  };

  const getSectionItems = async (contentfulId, totalCount, sectionIndex, locale) => {
    await fetch(config.resourceServer.contentfulApimAgentGqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${oktaAuth.getAccessToken()}`,
      },
      body: JSON.stringify({
        query: GET_PAGE_SECTION(sectionIndex),
        variables: { contentfulId : contentfulId, locale: locale, skip:sectionIndex},
      }),
    })
      .then((response) => response.json())
      .then(({ data, errors }) => {
        if (errors) {
          console.error(errors);
        }else{
          if(data?.page?.sectionsCollection?.items && data?.page?.sectionsCollection?.items.length > 0){
           
            data?.page?.sectionsCollection?.items.forEach((item) => {
   
              let itemType = item.typename;
              let itemTitleId = item.titleId;

              if(itemType === 'Section')
              {
                let result;
                result = getColumnSectionItems(item.componentsCollection.items[0].componentsCollection.items);

                switch (itemTitleId) {
                  case 'helpful-information' :
                    setHelpfulInfoSections(result);
                    break;
                  case 'helpful-information-quick-links' :
                    setQuickLinks(result);
                    break;
                  default:
                    break;
                }
              }else if(itemType === 'Paragraph'){
                setFaqDesc(item?.content?.json?.content[0]?.content[0]?.value);
              }
            })
          }

          if((sectionIndex) < totalCount || helpfulInfoSections?.length < totalCount) {
            getSectionItems(contentfulId, totalCount, sectionIndex + 1, locale);
          }
        }
      }
    );
  };

  const getColumnSectionItems = (sections) => {
    return sections.map((section) => {

      let title = section.displayTitle;
      let items = section.entriesCollection?.items.map((item) => {
          return {displayTitle: item.displayTitle, ...item.componentsCollection.items[0]};          
      });

      return {displayTitle: title, componentsCollection: items};
    })
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      let searchString = e.target.value.toLowerCase();

      if(searchString.length > 0)
        searchHelpfulInfos(searchString);
    }
  }

  const handleSearchIcon = () => {

    setSelectedInfo(null);

    const searchInput = document.getElementById("searchInput");
       
    let searchString = searchInput.value;
    if(searchString.length > 0)
      searchHelpfulInfos(searchString);
  }
  

  const searchHelpfulInfos = (searchString) => {
    searchResult.length = 0;
    setSearchText(searchString);

    helpfulInfoSections.forEach((section) => {
      section.componentsCollection.forEach(info => {
        if(info.typename.toLowerCase() === "paragraph"){
          searchParagraph(info, info.displayTitle, searchString);
        }
      })
    })

    setShowSearchResult(true);
  }

  const handleBackToFaqs = () => {
    setSearchResult([]);
    setSelectedInfo(null);
    setShowSearchResult(false);
  }

  const searchParagraph = (paragraph, displayTitle, searchText) => {

    if(paragraph.displayTitle?.toLowerCase().includes(searchText)){
      searchResult.push({"section" : displayTitle, "text" : displayTitle});
    }

    if(paragraph.content?.json?.nodeType.toLowerCase() === "document"){
      paragraph.content.json.content.map((document) => {
        return searchDocument(document, displayTitle, searchText);
      })
    }

    if(paragraph.nodeType?.toLowerCase() === "text"){
      if(paragraph.value.includes(searchText)){
        let index = paragraph.value.indexOf(searchText);
        if (index !== -1) {
          let extractedSubstring = "..." + paragraph.value.substring(index - 100, index + 100) + "...";
          searchResult.push({"section" : displayTitle, "text": extractedSubstring});
        }
      }
    }
  }

  const searchDocument = (document, displayTitle, searchText) => {
    
    if(document.nodeType.toLowerCase() === "paragraph"){
      return document.content.map((node) => {
        return searchParagraph(node, displayTitle, searchText);
      })
    }else if(document.nodeType.toLowerCase() === "document"){
      return searchDocument(document, displayTitle, searchText);
    }
  }

  useEffect(() => {

    if (setCurrentRoute) {
      setCurrentRoute({
        en: { path: "/en/helpful-info" },
        fr: { path: "/fr/helpful-info" },
      });
    }

    getHelpfulInfoPage();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection, selectedInfo, searchResult]);
  
  const onInfoClick = (section, info) => {
    setSelectedSection(section);
    setSelectedInfo(info);
  }

  const handleQuickLink = (linkInfo) => {
    helpfulInfoSections.forEach((section) => {
      section.componentsCollection.forEach((info) => {
        if(linkInfo.displayTitle === info.displayTitle){
          setSelectedInfo(info);
          setSelectedSection(section);
        }
      })
    })
  }
  
  return (
    <div className={styles.helpfulInfos}>
      <Helmet>
        <title>
          {`Agent Portal - ${RCL({ searchKey: "helpful-info-and-faqs" })}`}
        </title>
      </Helmet>

      <Breadcrumb pageSlug={"helpfulinfo"} locale={locale} />

      <div className={styles.helpfulInfosPageHeaderRow}>
        <div className={styles.helpfulInfosPageHeaderContainer}>
          <h1 className={styles.helpfulInfosHeader}>{RCL({ searchKey: "helpful-info-and-faqs" })}</h1>
          <div className={styles.helpfulInfosSearchBar}>
            <label className={styles.helpfulInfosSearchInputLabel} id="searchInputLabel" htmlFor="searchInput">{RCL({ searchKey: "find-what-you-are-looking-for-label" })}</label>
            <input 
              placeholder={RCL({ searchKey: "find-what-you-are-looking-for" }) } 
              className={styles.helpfulInfosSearchInput}
              onKeyDown={(e) => handleKeyDown(e)}
              id="searchInput"
            />
            <button aria-label={RCL({searchKey: "search"})} onClick={() => handleSearchIcon()} className={styles.helpfulInfosSearchButton}>
              <VWQIcon name="search" className={styles.helpfulInfosSearchIcon}/>
            </button>
          </div>
        </div>
        <div className={styles.headerLinkContainer}>
          { !showSearchResult && !selectedInfo && quickLinks && 
            <QuickLinkGrid data={quickLinks[0]?.componentsCollection} handleQuickLink={handleQuickLink} ></QuickLinkGrid>
          }
        </div>
      </div>
     
      <Section>
        <div>
          {(showSearchResult || selectedInfo) && (<button onClick={() => handleBackToFaqs()} className={styles.FaqHomeButton}>
            <VWQIcon name="chevron-left-micro"className={styles.FaqHomeIcon} /> 
            <span><RCL searchKey="faqs-home" /></span>
          </button>)}
          {!(showSearchResult || selectedInfo) && 
            <div className={styles.sectionHeader}>{faqDesc}</div>
          }
          {/* Category & info title list */}
          {!showSearchResult && !selectedInfo && helpfulInfoSections && <Grid data={helpfulInfoSections} onInfoClick={onInfoClick} />}
          {/* Selected info detail */}
          {!showSearchResult && selectedInfo && helpfulInfoSections && 
            (<div>
                <Row>
                  <Cell className={styles.categorySection}>
                    <div>
                      {helpfulInfoSections && helpfulInfoSections?.map((section) => {
                        return <SectionCategories 
                                  currentSection={section} 
                                  selectedInfo={selectedInfo}
                                  selectedSection={selectedSection} 
                                  setSelectedInfo={setSelectedInfo} 
                                  setSelectedSection={setSelectedSection} 
                                />
                        })
                      }
                    </div>
                  </Cell>
                  <Cell className={styles.infoSection}>
                    {selectedSection.componentsCollection.map((info) => {
                        return <SectionContent 
                                currentInfo={info} 
                                selectedInfo={selectedInfo} 
                                setSelectedInfo={setSelectedInfo} 
                                locale={locale === "fr" ? locale : "en-CA"} 
                              />
                      })
                    }
                  </Cell>
                </Row>
              </div>
            )
          }
          {/* Search result */}
          {showSearchResult && searchResult && (
            <div>
            <FaqSearchResult 
              searchText={searchText} 
              searchResult={searchResult} 
              helpfulInfoSections={helpfulInfoSections} 
              setSelectedInfo={setSelectedInfo} 
              setSelectedSection={setSelectedSection}
              setShowSearchResult={setShowSearchResult}/>
          </div>
          )}
        </div>
        <div>
          <NewsFeed locale={locale} />;
        </div>
      </Section>
    </div>
  );
};

export default HelpfulInfo;
